/**
 * @file Invoice.renderers.tsx
 * @description Field renderer configurations for Invoice forms
 * @module Invoices/Invoice
 *
 * Refactored to use factory functions that generate renderers based on configuration
 * Reduces code duplication from 752 lines to ~250 lines
 */

import { FieldRenderers } from "@avamae/formbuilder";
import { APP_ROUTES } from "config";

// Component imports
import { accessRightsRenderer } from "components/AccessRights/AccessRights";
import { searchListFieldRenderer } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import {
  singleMultiSelectFieldRenderer,
  singleMultiSelectReadOnlyFieldRenderer,
  singleMultiSelectWithLinkReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { dateTimeV2Renderer } from "components/DateTimeFieldV2/DateTimeFieldV2";
import { textAreaFieldRenderer } from "components/helpers/CustomFieldRenderers/TextAreaFieldRenderer/TextAreaFieldRenderer";
import { stringReadOnlyEditRenderer } from "components/helpers/CustomFieldRenderers/CustomString/StringReadOnlyEditFieldRenderer";
import { fileUploadFieldRenderer } from "components/helpers/FieldRenderers/FileUpload/fileUploadFieldRenderer";
import { booleanV2Renderer } from "components/BooleanV2/BooleanV2";
import { stringV2Renderer } from "components/helpers/CustomFieldRenderers/CustomString/StringV2";
import FilePreviewerFieldRenderer from "../components/FilePreviewerFieldRenderer/FilePreviewerFieldRenderer";
import { exchangeRatesTableRenderer } from "components/ExchangeRatesTable/ExchangeRatesTable";
import { booleanCheckboxRenderer } from "components/helpers/CustomFieldRenderers/BooleanCheckbox/BooleanCheckbox";
import { routingTableRenderer } from "components/RoutingTable/RoutingTable";
import { multiFileUploadFieldRenderer } from "components/helpers/FieldRenderers/FileUpload/multiFileUploadRenderer";
import { customFieldRender } from "components/helpers/CustomFieldRenderers/CustomFormFields/CustomFieldRender";
import { currencyRenderer } from "components/helpers/FieldRenderers/CurrencyField/CurrencyField";

// Local imports
import { INVOICE_ACCEPTED_FILE_TYPES } from "./Invoice.config";
import type { InvoiceFieldsCreate, InvoiceFieldsDetails } from "./Invoice.types";
import {
  endpointConfig,
  linkConfig,
  actionLinkTextConfig,
  onLinkClick,
  onActionLinkClick,
} from "./Invoice.config";

/**
 * Configuration for search list renderers with links
 * Reused across multiple field types
 */
const searchListConfig = {
  searchEndpoint: endpointConfig,
  linkTextConfig: linkConfig,
  onLinkClick: onLinkClick,
  actionLinkConfig: actionLinkTextConfig,
  handleActionLinkClicked: onActionLinkClick,
};

/**
 * Configuration for associated users link
 * Used in read-only multi-select fields
 */
const associatedUsersLinkConfig = {
  listAssociatedUsers: {
    filterFieldName: "Invoices_InvoiceIDs",
    filterOperator: "CONTAINS" as const,
    filterValueFieldName: "id",
    text: "View all users",
    route: APP_ROUTES.platformUsers,
  },
};

/**
 * Base renderers shared across all invoice forms
 * These renderers are common regardless of action type or permissions
 */
const getBaseRenderers = (readOnly: boolean) => ({
  accessrights: accessRightsRenderer(readOnly),
  datetime: dateTimeV2Renderer(readOnly),
  datetimereadonly: dateTimeV2Renderer(false, true),
  textarea: textAreaFieldRenderer(
    readOnly,
    readOnly ? undefined : { description: "Type your text here", hintText: "Type your text here" },
    readOnly ? undefined : { description: 1000, hintText: 1000 },
    readOnly
  ),
  textareareadonly: textAreaFieldRenderer(false, undefined, undefined, true),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
  numberreadonly: stringReadOnlyEditRenderer(null, null, true),
  decimalreadonly: stringReadOnlyEditRenderer(null, null, true),
  booleanreadonly: booleanV2Renderer(false, undefined, undefined, true),
  booleancheckbox: booleanCheckboxRenderer(readOnly),
  booleancheckboxreadonly: booleanCheckboxRenderer(false, true),
  customfields: customFieldRender(false),
  exchangeratestable: exchangeRatesTableRenderer(readOnly),
  routingtable: routingTableRenderer(readOnly, "id", true),
});

/**
 * Search list field renderers
 * Handles single/multi select with or without links
 */
const getSearchListRenderers = (readOnly: boolean, isUpdate: boolean = false) => ({
  singleselectsearchlist: searchListFieldRenderer("singleselect", undefined, null, readOnly),
  singleselectsearchlistreadonly: searchListFieldRenderer(
    "singleselect",
    undefined,
    null,
    false,
    true
  ),
  singleselectsearchlistwithlinks: readOnly
    ? singleMultiSelectReadOnlyFieldRenderer("multiselect")
    : searchListFieldRenderer(
        "singleSelectWithLinks",
        searchListConfig,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        isUpdate ? "40px" : undefined,
        isUpdate
      ),
  singleselectsearchlistwithlinksreadonly: searchListFieldRenderer(
    "singleSelectWithLinks",
    searchListConfig,
    undefined,
    true
  ),
  multiselectsearchlistwithlinks: readOnly
    ? singleMultiSelectWithLinkReadOnlyFieldRenderer(associatedUsersLinkConfig)
    : searchListFieldRenderer(
        "multiselectWithLinks",
        searchListConfig,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        isUpdate,
        isUpdate ? "40px" : undefined,
        isUpdate
      ),
  multiselectsearchlistwithlinksreadonly: searchListFieldRenderer(
    "multiselectWithLinks",
    searchListConfig,
    undefined,
    true
  ),
});

/**
 * Single/multi select field renderers
 * Only present in update forms
 */
const getSelectRenderers = () => ({
  singleselect: singleMultiSelectFieldRenderer(
    "singleselect",
    undefined,
    undefined,
    undefined,
    false,
    true
  ),
  multiselect: singleMultiSelectFieldRenderer(
    "multiselect",
    undefined,
    undefined,
    undefined,
    false,
    true
  ),
  singleselectreadonly: singleMultiSelectFieldRenderer(
    "singleselect",
    undefined,
    undefined,
    undefined,
    true
  ),
  multiselectreadonly: singleMultiSelectFieldRenderer(
    "multiselect",
    undefined,
    undefined,
    undefined,
    true
  ),
});

/**
 * Field renderers for creating new invoices
 */
export const rightsRendererCreate = (): FieldRenderers<InvoiceFieldsCreate> => ({
  ...getBaseRenderers(false),
  ...getSearchListRenderers(false, false),
  file: fileUploadFieldRenderer(false, { files: INVOICE_ACCEPTED_FILE_TYPES }),
  pdfviewercolumn: multiFileUploadFieldRenderer({
    readOnly: false,
    fieldNames: {
      singleImageOrFile: {
        imageOrFile: "file",
        imageOrFileName: "fileName",
        imageOrFileDownloadUri: "fileDownloadUri",
        imageOrFileExtension: "fileExtension",
      },
      multipleImagesOrFiles: "files",
    },
    isMulti: true,
    acceptedFileTypes: { files: INVOICE_ACCEPTED_FILE_TYPES },
    resetToClearAll: true,
  }),
  currency: currencyRenderer(false, false, true),
  currencyreadonly: currencyRenderer(true, false, true),
});

/**
 * Field renderers for updating invoices WITH document update permissions
 * Used for action types: "update", "update-apclerk"
 */
export const rightsRendererUpdateWithUpdateDocumentPermissions =
  (): FieldRenderers<InvoiceFieldsDetails> => ({
    ...getBaseRenderers(false),
    ...getSearchListRenderers(false, true),
    ...getSelectRenderers(),
    file: fileUploadFieldRenderer(false, { files: ["image/jpg", "image/png"] }),
    string: stringV2Renderer(false, null, null),
    integer: stringV2Renderer(false, null, null, true),
    number: stringV2Renderer(false, null, null, true, undefined, undefined, true),
    decimal: stringV2Renderer(false, null, null, true, undefined, undefined, true),
    pdfviewercolumn: (_key, field) => (
      <FilePreviewerFieldRenderer field={field} enablePrint enableDownload enableUpload />
    ),
    currency: currencyRenderer(false, false, true),
    currencyreadonly: currencyRenderer(true, false),
  });

/**
 * Field renderers for updating invoices WITHOUT document update permissions
 * Used for action types: "update-productmanager", "update-apcoordinator"
 */
export const rightsRendererUpdateWithoutUpdateDocumentPermissions =
  (): FieldRenderers<InvoiceFieldsDetails> => ({
    ...getBaseRenderers(false),
    ...getSearchListRenderers(false, true),
    ...getSelectRenderers(),
    file: fileUploadFieldRenderer(false, { files: ["image/jpg", "image/png"] }),
    string: stringV2Renderer(false, null, null, undefined, undefined, undefined, false),
    integer: stringV2Renderer(false, null, null, true, undefined, undefined, true),
    number: stringV2Renderer(false, null, null, true, undefined, undefined, true),
    decimal: stringV2Renderer(false, null, null, true, undefined, undefined, true),
    pdfviewercolumn: (_key, field) => (
      <FilePreviewerFieldRenderer field={field} enablePrint enableDownload />
    ),
    currency: currencyRenderer(false, false, true),
    currencyreadonly: currencyRenderer(true, false),
  });

/**
 * Read-only field renderers for create action
 */
export const readOnlyRightsRendererCreate = (): FieldRenderers<InvoiceFieldsCreate> => ({
  ...getBaseRenderers(true),
  ...getSearchListRenderers(true, false),
  file: fileUploadFieldRenderer(true),
  pdfviewercolumn: (_key, field) => (
    <FilePreviewerFieldRenderer field={field} enablePrint enableDownload />
  ),
  currency: currencyRenderer(true, false, true),
  currencyreadonly: currencyRenderer(true, false),
});

/**
 * Read-only field renderers for view action
 */
export const readOnlyRightsRendererView = (): FieldRenderers<InvoiceFieldsDetails> => ({
  ...getBaseRenderers(true),
  ...getSearchListRenderers(true, false),
  ...getSelectRenderers(),
  file: fileUploadFieldRenderer(true),
  pdfviewercolumn: (_key, field) => (
    <FilePreviewerFieldRenderer field={field} enablePrint enableDownload />
  ),
  currency: currencyRenderer(true, true, true),
  currencyreadonly: currencyRenderer(true, true),
});

/**
 * Read-only field renderers for update action
 */
export const readOnlyRightsRendererUpdate = (): FieldRenderers<InvoiceFieldsDetails> => ({
  ...getBaseRenderers(true),
  ...getSearchListRenderers(true, false),
  ...getSelectRenderers(),
  file: fileUploadFieldRenderer(true),
  pdfviewercolumn: (_key, field) => (
    <FilePreviewerFieldRenderer field={field} enablePrint enableDownload />
  ),
  currency: currencyRenderer(true, false, true),
  currencyreadonly: currencyRenderer(true, false),
});
